import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { chartTooltipProps } from '@/lib/chartTheme';
import type { MechanicUtilization } from '@/hooks';

interface MechanicUtilizationChartProps {
  data?: MechanicUtilization[];
}

export function MechanicUtilizationChart({ data = [] }: MechanicUtilizationChartProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const getBarColor = (utilization: number) => {
    if (utilization >= 85) return theme.palette.success.main;
    if (utilization >= 70) return theme.palette.primary.light;
    if (utilization >= 50) return theme.palette.warning.dark;
    return theme.palette.error.main;
  };

  const averageUtilization = data.length > 0
    ? Math.round(
        data.reduce((sum, m) => sum + m.utilization, 0) /
          data.length
      )
    : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('mechanicUtilization')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('today')}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight={600} color="primary.main">
              {averageUtilization}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('averageUtilization')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: 250 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
                horizontal={false}
              />
              <XAxis
                type="number"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                width={70}
              />
              <Tooltip
                {...chartTooltipProps(theme)}
                formatter={(value: number, _name: string, props) => {
                  const data = props.payload as MechanicUtilization;
                  return [
                    `${value}% (${data.hoursWorked}h / ${data.hoursAvailable}h)`,
                    t('utilization'),
                  ];
                }}
              />
              <ReferenceLine
                x={80}
                stroke={theme.palette.success.main}
                strokeDasharray="3 3"
                label={{
                  value: t('target'),
                  position: 'top',
                  fontSize: 10,
                  fill: theme.palette.text.secondary,
                }}
              />
              <Bar
                dataKey="utilization"
                radius={[0, 4, 4, 0]}
                barSize={20}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.utilization)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
